import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import CampaignManagementForm from 'components/forms/CampaignManagementDetails/CampaignManagement.tsx';
import Back from 'components/Back';
import ApiErrorDetails from 'components/ApiErrorDetails/index.tsx';
import useCampaignLock from 'hooks/useCampaignLock.ts';
import useProtectRoute from 'hooks/useProtectRoute.ts';
import useCampaign from 'hooks/useCampaign.ts';
import useUpdateCampaign from 'queries/mutate/updateCampaign.ts';
import { ApiError } from 'schemas/ApiError.schema.ts';
import { CampaignManagementDetails } from 'schemas/CampaignConfig.schema.ts';
import { API_UPDATE_CAMPAIGN_ERROR } from 'constants/errorMessages.ts';
import { ICampaignLevelParams } from 'utils/parameterUtils';
import useFetchTemplateForCampaign from 'queries/fetch/fetchTemplateForCampaign.ts';
import featureFlags from 'services/featureFlags.ts';
import ProgressIndicator from 'components/ProgressIndicator';
import getBackToUrl from 'utils/campaignUtils';

export function CampaignManagementDetailsPage() {
  const { campaign } = useCampaign();
  const { teamId, campaignId } = useParams() as ICampaignLevelParams;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const updateCampaign = useUpdateCampaign();
  const [error, setError] = useState<ApiError | null>(null);
  const { lockCampaignIfRequired, error: lockError, isLoading: campaignIsLoading } = useCampaignLock({ teamId, campaignId });
  const campaignTemplate = useFetchTemplateForCampaign({
    teamId,
    campaignId,
    isEnabled: Boolean(campaign.CampaignConfig.Template),
  });

  useProtectRoute();
  const initialRenderRef = useRef(true);

  const approverDefault = campaignTemplate.data?.['CampaignConfig.Approver.input.default']?.trim();
  const managerDefault = campaignTemplate.data?.['CampaignConfig.Manager.input.default']?.trim();
  const reviewerDefault = campaignTemplate.data?.['CampaignConfig.Reviewer.input.default']?.trim();

  const searchParams = new URLSearchParams(location.search);

  const handleSuccessfulSubmission = async (managementDetails: CampaignManagementDetails) => {
    const updatedCampaign = {
      ...campaign,
      CampaignConfig: {
        ...campaign.CampaignConfig,
        ...managementDetails,
      },
    };

    try {
      setIsSubmitting(true);
      await updateCampaign.mutateAsync({
        campaignId,
        teamId: teamId!,
        campaign: updatedCampaign,
      });

      const params = new URLSearchParams(location.search);
      if (params.get('mode') === 'new') {
        navigate(`/${teamId}/campaign/${campaignId}/message-plans`);
        return;
      }

      navigate(`/${teamId}/campaign/${campaignId}/summary`);
    } catch {
      setError({ message: API_UPDATE_CAMPAIGN_ERROR });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!campaignIsLoading && initialRenderRef.current) {
      initialRenderRef.current = false;
      lockCampaignIfRequired();
    }
  }, [lockCampaignIfRequired, campaignIsLoading]);

  if (
    featureFlags.campaignTemplates
    && Boolean(campaign.CampaignConfig.Template)
    && !campaignTemplate.isFetched
  ) {
    return <ProgressIndicator />;
  }

  return (
    <div>
      <div className="nhsuk-u-margin-bottom-6">
        <Back label="Campaign summary" backTo={getBackToUrl(searchParams, teamId, campaignId)} />
      </div>
      <CampaignManagementForm
        onSuccessfulSubmission={handleSuccessfulSubmission}
        isSubmitting={isSubmitting}
        campaign={{
          ...campaign,
          CampaignConfig: {
            ...campaign.CampaignConfig,
            Approver: approverDefault ? approverDefault.split(',') : campaign.CampaignConfig.Approver ?? [],
            Manager: managerDefault ? managerDefault.split(',') : campaign.CampaignConfig.Manager ?? [],
            Reviewer: reviewerDefault ? reviewerDefault.split(',') : campaign.CampaignConfig.Reviewer ?? [],
          },
        }}
        template={campaignTemplate.data}
      />
      <ApiErrorDetails apiError={error || lockError} />
    </div>
  );
}

export default CampaignManagementDetailsPage;
